/**
 * Blog Tabs - Tab Switching & AJAX Pagination with URL State
 * Handles switching between blog tabs without page reload
 * Handles pagination within each tab via AJAX
 * Updates URL for shareable links and browser history
 */

class BlogTabs {
    constructor(container) {
        this.container = container;
        this.tabs = container.querySelectorAll('.blog-tab');
        this.panels = container.querySelectorAll('.blog-tab-panel');
        this.indicator = container.querySelector('.blog-tabs-nav__indicator');
        this.nav = container.querySelector('.blog-tabs-nav');

        // ResizeObserver for tracking precise element dimensions changes
        this.resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => this.updateIndicatorPosition(false));
        });

        if (this.tabs.length > 0) {
            this.setupLabels();
            this.bindEvents();
            this.bindPaginationEvents();
            this.handlePopState();
            this.restoreStateFromURL();

            // We use requestAnimationFrame to ensure DOM is ready for calculations
            requestAnimationFrame(() => {
                this.updateIndicatorPosition(false);
            });

            // Recalculate after everything (images, fonts) is loaded
            window.addEventListener('load', () => {
                this.updateIndicatorPosition(false);
            });

            //  Keep this for responsive recalculation
            window.addEventListener('resize', () => {
                this.updateIndicatorPosition(false);
            });

            // Update indicator on scroll (for horizontal scrolling)
            this.nav.addEventListener('scroll', () => {
                this.updateIndicatorPosition(false);
            });
        }
    }

    /**
     * Sets up data-label attribute on labels to match text content.
     * This is used by CSS to reserve space for the bold font-weight state,
     * preventing layout shifts ("shaking") when switching tabs.
     */
    setupLabels() {
        this.tabs.forEach(tab => {
            const label = tab.querySelector('.blog-tab__label');
            if (label && label.textContent) {
                // Determine the clean text content (trimming whitespace)
                // This attribute is used by CSS ::after pseudo-element
                label.setAttribute('data-label', label.textContent.trim());
            }
        });
    }


    /**
     * Updates the position and width of the active tab indicator (Yellow Shape).
     * 
     * Logic:
     * - Mobile (<= 767px): Uses static CSS shape (no JS calculation).
     * - Desktop: Calculates position relative to the image and extends a "tail" to the left.
     *   The width wraps the content (Image to Text) + the tail extension.
     * 
     * @param {boolean} animate - Whether to apply transition animation.
     */
    updateIndicatorPosition(animate = true) {
        // MOBILE OPTIMIZATION:
        // On mobile devices, we use a static CSS pseudo-element for performance and
        // to match specific design requirements (trapezoid shape).
        // Therefore, we exit early to skip JS calculations.
        if (window.innerWidth <= 767) return;

        const activeTab = this.container.querySelector('.blog-tab.active');
        // Safety check: ensure all necessary elements exist before proceeding
        if (!activeTab || !this.indicator || !this.nav) return;

        // --- 1. CONFIGURATION ---
        // TAIL_EXTENSION: Pixels to extend the indicator to the left of the image.
        // Matches the "short left tail" design request.
        const TAIL_EXTENSION = 15;

        // BUFFER_RIGHT: Extra padding on the right side of the text label.
        const BUFFER_RIGHT = 10;

        // --- 2. MEASUREMENTS ---
        const navRect = this.nav.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();

        // Get sub-elements to calculate precise content bounds
        const label = activeTab.querySelector('.blog-tab__label');
        const imageWrapper = activeTab.querySelector('.blog-tab__image-wrapper');

        // Fallback to tabRect if specific elements are missing (rare edge case)
        const labelRect = label ? label.getBoundingClientRect() : tabRect;
        const imageRect = imageWrapper ? imageWrapper.getBoundingClientRect() : tabRect;

        const currentScroll = this.nav.scrollLeft;

        // --- 3. CALCULATION (DESKTOP) ---
        // We assume Horizontal Layout: [Image] -> [Text]

        // Start Point (Left): 
        // We align relative to the *Image's Left Edge*.
        // Then we subtract the TAIL_EXTENSION to create the "tail" effect.
        const contentLeftAbs = imageRect.left;
        const finalLeft = (contentLeftAbs - navRect.left) + currentScroll - TAIL_EXTENSION;

        // End Point (Right) & Width:
        // Width covers the distance from the Image Left to the Label Right.
        // We add the TAIL_EXTENSION (since we shifted left) and the BUFFER_RIGHT.
        const contentRightAbs = labelRect.right;
        const baseContentWidth = contentRightAbs - contentLeftAbs;

        const finalWidth = TAIL_EXTENSION + baseContentWidth + BUFFER_RIGHT;

        // Vertical Alignment:
        // specific height (90px) and centered vertically.
        const finalTop = '50%';
        const transform = 'translateY(-50%)';
        const finalHeight = '90px';

        // --- 4. APPLY STYLES ---
        if (!animate) {
            this.indicator.style.transition = 'none';
        }

        this.indicator.style.left = `${finalLeft}px`;
        this.indicator.style.width = `${finalWidth}px`;
        this.indicator.style.top = finalTop;
        this.indicator.style.height = finalHeight;
        this.indicator.style.transform = transform;

        if (!animate) {
            // Force reflow to flush style changes before re-enabling transitions
            this.indicator.offsetHeight;
            this.indicator.style.transition = '';
        }
    }

    bindEvents() {
        this.tabs.forEach(tab => {
            tab.addEventListener('click', (e) => {
                e.preventDefault();
                this.switchTab(tab, true); // true = update URL
            });
        });

        // Arrow Navigation for Mobile (Re-query to ensure they are found)
        const prevArrow = this.container.querySelector('.blog-tabs-arrow--prev');
        const nextArrow = this.container.querySelector('.blog-tabs-arrow--next');

        if (prevArrow && nextArrow) {
            prevArrow.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation(); // prevent bubbling issues
                const scrollAmount = this.nav.clientWidth / 3; // Scroll by one item
                this.nav.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            });

            nextArrow.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const scrollAmount = this.nav.clientWidth / 3; // Scroll by one item
                this.nav.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            });

            // Initial check
            this.checkArrowVisibility(prevArrow, nextArrow);

            // Check on scroll
            this.nav.addEventListener('scroll', () => {
                this.checkArrowVisibility(prevArrow, nextArrow);
            });

            // Check on resize
            window.addEventListener('resize', () => {
                this.checkArrowVisibility(prevArrow, nextArrow);
            });
        }
    }

    checkArrowVisibility(prevArrow, nextArrow) {
        if (!this.nav) return;

        // Define tolerance usually 1px
        const tolerance = 2;
        const scrollLeft = this.nav.scrollLeft;
        const maxScrollLeft = this.nav.scrollWidth - this.nav.clientWidth;

        // Disable Left Arrow if at start
        if (scrollLeft <= tolerance) {
            prevArrow.setAttribute('disabled', 'true');
            prevArrow.classList.add('disabled');
        } else {
            prevArrow.removeAttribute('disabled');
            prevArrow.classList.remove('disabled');
        }

        // Disable Right Arrow if at end
        if (Math.abs(scrollLeft - maxScrollLeft) <= tolerance || maxScrollLeft <= 0) {
            nextArrow.setAttribute('disabled', 'true');
            nextArrow.classList.add('disabled');
        } else {
            nextArrow.removeAttribute('disabled');
            nextArrow.classList.remove('disabled');
        }
    }

    bindPaginationEvents() {
        // Bind click events to all pagination links within blog tab panels
        this.panels.forEach(panel => {
            const paginationWrapper = panel.querySelector('.blog-tab-pagination');
            if (paginationWrapper) {
                paginationWrapper.addEventListener('click', (e) => {
                    const link = e.target.closest('a');
                    if (link && link.href) {
                        e.preventDefault();
                        this.handlePagination(panel, link.href, true); // true = update URL
                    }
                });
            }
        });
    }

    handlePopState() {
        // Handle browser back/forward buttons
        window.addEventListener('popstate', () => {
            this.restoreStateFromURL();
        });
    }

    restoreStateFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');
        const pageParam = urlParams.get('page') || '1';

        if (tabParam) {
            // Find the tab button for this blog
            const targetTab = Array.from(this.tabs).find(tab => tab.dataset.blog === tabParam);
            if (targetTab) {
                this.switchTab(targetTab, false); // false = don't update URL (we're restoring from URL)

                // If page > 1, fetch that page
                if (pageParam !== '1') {
                    const panel = this.container.querySelector(`.blog-tab-panel[data-blog="${tabParam}"]`);
                    if (panel && panel.dataset.blogUrl) {
                        this.handlePagination(panel, `${panel.dataset.blogUrl}?page=${pageParam}`, false);
                    }
                }
            }
        }
    }

    updateURL(blogHandle, page) {
        const url = new URL(window.location);

        if (blogHandle === 'all') {
            url.searchParams.delete('tab');
            url.searchParams.delete('page');
        } else {
            url.searchParams.set('tab', blogHandle);
            if (page && page !== '1') {
                url.searchParams.set('page', page);
            } else {
                url.searchParams.delete('page');
            }
        }

        history.pushState({ tab: blogHandle, page: page }, '', url);
    }

    switchTab(selectedTab, updateUrl = true) {
        const blogHandle = selectedTab.dataset.blog;

        // Update active tab styling
        this.tabs.forEach(tab => {
            tab.classList.remove('active');
            tab.setAttribute('aria-selected', 'false');
        });
        selectedTab.classList.add('active');
        selectedTab.setAttribute('aria-selected', 'true');

        // Update active panel visibility and reset to page 1
        this.panels.forEach(panel => {
            if (panel.dataset.blog === blogHandle) {
                panel.classList.add('active');
                panel.setAttribute('aria-hidden', 'false');

                // Reset to page 1 when switching tabs (if not already on page 1)
                const contentWrapper = panel.querySelector('.blog-tab-content');
                if (contentWrapper) {
                    const currentPage = parseInt(contentWrapper.dataset.currentPage || '1');
                    if (currentPage !== 1) {
                        // Fetch page 1 for this blog
                        const blogUrl = panel.dataset.blogUrl;
                        if (blogUrl) {
                            this.handlePagination(panel, blogUrl + '?page=1', false);
                        }
                    }
                }
            } else {
                panel.classList.remove('active');
                panel.setAttribute('aria-hidden', 'true');
            }
        });

        // Update URL
        if (updateUrl) {
            this.updateURL(blogHandle, '1');
        }

        // Observe the new active tab's label for size changes
        this.observeActiveTab(selectedTab);

        // Update indicator position
        this.updateIndicatorPosition();
    }

    observeActiveTab(tab) {
        // Disconnect previous observation
        this.resizeObserver.disconnect();

        // Observe the label of the active tab
        const label = tab.querySelector('.blog-tab__label');
        if (label) {
            this.resizeObserver.observe(label);
        }
    }

    async handlePagination(panel, url, updateUrl = true) {
        const blogHandle = panel.dataset.blog;
        const blogUrl = panel.dataset.blogUrl;

        // Extract page number from URL
        const urlObj = new URL(url, window.location.origin);
        const page = urlObj.searchParams.get('page') || '1';

        try {
            // Fetch the blog page
            const response = await fetch(`${blogUrl}?page=${page}`);

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const html = await response.text();

            // Parse the HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Find the articles from main-blog section
            const articlesFromPage = doc.querySelector('.blog-articles');
            const paginationFromPage = doc.querySelector('.pagination-wrapper');

            if (articlesFromPage) {
                // Update articles in the panel
                const contentWrapper = panel.querySelector('.blog-tab-content');
                const articlesContainer = panel.querySelector('.blog-articles');
                const paginationContainer = panel.querySelector('.blog-tab-pagination');

                if (articlesContainer) {
                    articlesContainer.innerHTML = articlesFromPage.innerHTML;
                }

                // Update pagination
                if (paginationContainer && paginationFromPage) {
                    paginationContainer.innerHTML = paginationFromPage.innerHTML;
                    // Re-bind pagination events for new links
                    this.rebindPaginationForPanel(panel);
                } else if (paginationContainer && !paginationFromPage) {
                    // No more pagination needed (single page)
                    paginationContainer.innerHTML = '';
                }

                // Update current page data attribute
                if (contentWrapper) {
                    contentWrapper.dataset.currentPage = page;
                }

                // Update URL
                if (updateUrl) {
                    this.updateURL(blogHandle, page);
                }

                // Scroll to top of panel
                panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } catch (error) {
            console.error('Error fetching blog page:', error);
            // Fallback: navigate to the URL normally
            window.location.href = url;
        }
    }

    rebindPaginationForPanel(panel) {
        const paginationWrapper = panel.querySelector('.blog-tab-pagination');
        if (paginationWrapper) {
            // Remove existing listeners by cloning
            const newPaginationWrapper = paginationWrapper.cloneNode(true);
            paginationWrapper.parentNode.replaceChild(newPaginationWrapper, paginationWrapper);

            // Add new listener
            newPaginationWrapper.addEventListener('click', (e) => {
                const link = e.target.closest('a');
                if (link && link.href) {
                    e.preventDefault();
                    this.handlePagination(panel, link.href, true);
                }
            });
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    const blogTabsWrappers = document.querySelectorAll('.blog-tabs-wrapper');

    blogTabsWrappers.forEach(wrapper => {
        new BlogTabs(wrapper);
    });
});

// Re-initialize on Shopify section reload (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
        const section = event.target;
        const wrapper = section.querySelector('.blog-tabs-wrapper');

        if (wrapper) {
            new BlogTabs(wrapper);
        }
    });
}